/**
 * Button — Core action element
 *
 * Variants:
 *   - variant: primary, secondary, secondaryoutline, outline, destructive, ghost
 *   - size: sm, md, lg
 *
 * Supports:
 *   - Loading state via `loading` prop (shows spinner, disables press)
 *   - Icons via children composition
 *   - String children auto-wrapped in ButtonText
 */
import { styled, XStack, Text as TamaguiText, type GetProps } from 'tamagui'
import { Spinner } from './Spinner'

const ButtonFrame = styled(XStack, {
  name: 'Button',
  role: 'button',
  ai: 'center',
  jc: 'center',
  gap: '$sm',
  br: '$md',
  cursor: 'pointer',
  userSelect: 'none',

  variants: {
    variant: {
      primary: {
        bg: '$colorPrimary',
        pressStyle: { opacity: 0.7, scale: 0.98 },
        hoverStyle: { opacity: 0.85 },
      },
      secondary: {
        bg: '$backgroundCard',
        pressStyle: { opacity: 0.7, scale: 0.98 },
        hoverStyle: { opacity: 0.85 },
      },
      secondaryoutline: {
        bg: '$backgroundCard',
        bw: 2,
        bc: '$borderColor',
        borderStyle: 'solid',
        pressStyle: { opacity: 0.7, scale: 0.98 },
        hoverStyle: { opacity: 0.85 },
      },
      outline: {
        bg: 'rgba(255,255,255,0)',
        bw: 2,
        bc: '$colorPrimary',
        pressStyle: { bg: '$colorPrimaryMuted', scale: 0.98 },
        hoverStyle: { bg: '$colorPrimaryMuted' },
      },
      destructive: {
        bg: '$colorError',
        pressStyle: { opacity: 0.7, scale: 0.98 },
        hoverStyle: { opacity: 0.85 },
      },
      destructiveoutline: {
        bg: 'rgba(255,255,255,0)',
        bw: 2,
        bc: '$colorError',
        borderStyle: 'solid',
        pressStyle: { opacity: 0.7, scale: 0.98 },
        hoverStyle: { opacity: 0.85 },
      },
      ghost: {
        bg: 'rgba(255,255,255,0)',
        pressStyle: { bg: '$backgroundSecondary', scale: 0.98 },
        hoverStyle: { bg: '$backgroundSecondary' },
      },
    },

    size: {
      sm: {
        px: '$sm',
        py: '$sm',
        br: '$sm',
      },
      md: {
        px: '$lg',
        py: 14,
      },
      lg: {
        px: '$xl',
        py: '$md',
      },
    },

    fullWidth: {
      true: {
        w: '100%',
      },
    },

    disabled: {
      true: {
        opacity: 0.5,
        pointerEvents: 'none',
      },
    },
  } as const,

  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
})

/**
 * ButtonText — styled directly from TamaguiText (not our Text element).
 *
 * Our Text element has `tone: 'default'` → `color: '$color'` which
 * overrides variant-specified colors. Using TamaguiText avoids this.
 *
 * Pass `variant` explicitly to match the parent Button's variant.
 */
export const ButtonText = styled(TamaguiText, {
  name: 'ButtonText',
  fontFamily: '$body',
  fontSize: '$4',
  lineHeight: '$4',
  fontWeight: '600',
  color: '$colorInverse',
  userSelect: 'none',

  variants: {
    variant: {
      primary: { color: '$colorInverse' },
      secondary: { color: '$color' },
      secondaryoutline: { color: '$color' },
      outline: { color: '$colorPrimary' },
      destructive: { color: '$colorInverse' },
      destructiveoutline: { color: '$colorError' },
      ghost: { color: '$color' },
    },
    size: {
      sm: { fontSize: '$3' },
      md: { fontSize: '$4' },
      lg: { fontSize: '$4' },
    },
  } as const,

  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
})

type ButtonFrameProps = GetProps<typeof ButtonFrame>

interface ButtonProps extends ButtonFrameProps {
  loading?: boolean
}

export const Button = ButtonFrame.styleable<ButtonProps>(
  ({ loading, disabled, children, variant, size, ...props }, ref) => {
    const resolvedVariant = variant ?? 'primary'
    return (
      <ButtonFrame
        ref={ref}
        transition="quick"
        animateOnly={['opacity', 'transform', 'background-color']}
        disabled={loading || disabled}
        variant={resolvedVariant}
        {...(size ? { size } : {})}
        {...props}
      >
        {loading ? (
          <Spinner
            size="small"
            color={
              resolvedVariant === 'destructiveoutline'
                ? '$colorError'
                : resolvedVariant === 'outline' ||
                    resolvedVariant === 'ghost' ||
                    resolvedVariant === 'secondary' ||
                    resolvedVariant === 'secondaryoutline'
                  ? '$colorPrimary'
                  : '$colorInverse'
            }
          />
        ) : typeof children === 'string' ? (
          <ButtonText variant={resolvedVariant} {...(size ? { size } : {})}>{children}</ButtonText>
        ) : (
          children
        )}
      </ButtonFrame>
    )
  },
)
